// Mindmap — Session 19AE: flattened from recursive nodes to flat list +
// parentId pattern (same as TopicNode hierarchy).
//
// Why flatten:
//   - LLM emits flat lists much more reliably than recursive structures.
//     Deep recursion is the #1 cause of "missing children" / "duplicate nodes"
//     hallucinations.
//   - JSON Schema for the LLM is far simpler (no z.lazy / cycle reference).
//   - Frontend reconstructs the tree at render time via single pass.
//
// 19BH Day 2 Step 2.4: ItemTags base applied. v3 had caseId (required string,
// 1 mindmap per chapter). v4 uses caseIds[] · still typically 1 element ·
// crossChapterAllowed=false per MANIFEST. Singular caseId stays as deprecated
// optional alias.
//
// Migration path: legacy recursive results in IDB are detected by presence of
// `root` field; converted to flat by `migrateMindmap()` in storage/idb.ts.
import { z } from 'zod';
import { ItemTagsSchema, coerceItemTagsV3ToV4 } from './item-tags.js';
import { ConceptOmissionSchema } from './concept-omission.js';
export const MindmapNodeSchema = z.object({
    id: z.string().min(1)
        .describe('Unique slug within this mindmap. Convention: snake_case path-like id (e.g. `central_thesis`, `central_thesis__supporting_arg_1`).'),
    label: z.string().min(3).max(200)
        .describe('Short text shown on the node (3-200 chars). Phrase, not full sentence. Markdown stripped at render. UI truncates via CSS; schema ceiling is a safety net, not a layout constraint. Relaxed 2026-06-02 from max(80) — LLM frequently exceeds 80 chars for concept-heavy labels.'),
    parentId: z.string().nullable()
        .describe('Parent node id within this mindmap. null only for the root node (exactly one root per mindmap).'),
});
export const MindmapSchema = z.preprocess(coerceItemTagsV3ToV4, ItemTagsSchema.extend({
    id: z.string().min(1)
        .describe('Mindmap slug. Convention: `mm_<chapterSlug>` (e.g. `mm_logic_proofs`).'),
    /** @deprecated v3 alias of caseIds[0]. Mindmap is typically 1 chapter · caseIds.length === 1. */
    caseId: z.string().min(1).nullable().optional()
        .describe('@deprecated v3 alias of caseIds[0]. Use caseIds[] · mindmap is single-chapter typical.'),
    title: z.string().min(1)
        .describe('Mindmap title shown in header (e.g. "Logic & Proofs Overview").'),
    nodes: z.array(MindmapNodeSchema).min(5)
        .describe('Flat list of nodes. ≥5 nodes (was min 1 — bumped Session 19AO; a single-root mindmap with one branch is degenerate). Exactly one node has parentId=null (the root). All other nodes reference an existing id via parentId. Render-time reconstruction builds the tree.'),
    omittedConcepts: z.array(ConceptOmissionSchema).nullish()
        .describe('K18 soft escape · concepts the LLM declared materially insufficient for this stage. Mindmap MANIFEST entry has acceptsOmissionReason=false (full coverage required) but the field stays in schema for uniform shape.'),
}).passthrough());
export const MindmapsSchema = z.array(MindmapSchema);
/** Validate a mindmap's flat-with-parentId structure. */
export function validateMindmap(m) {
    const issues = [];
    const ids = new Set();
    let rootCount = 0;
    for (const n of m.nodes) {
        if (ids.has(n.id))
            issues.push(`duplicate node id in mindmap ${m.id}: ${n.id}`);
        ids.add(n.id);
        if (n.parentId === null)
            rootCount++;
    }
    if (rootCount === 0)
        issues.push(`mindmap ${m.id}: no root node (exactly one node should have parentId=null)`);
    if (rootCount > 1)
        issues.push(`mindmap ${m.id}: ${rootCount} root nodes (must be exactly 1)`);
    for (const n of m.nodes) {
        if (n.parentId !== null && !ids.has(n.parentId)) {
            issues.push(`mindmap ${m.id} node ${n.id}: parentId ${n.parentId} references missing node`);
        }
    }
    return issues;
}
export function buildMindmapTree(m) {
    const byId = new Map();
    for (const n of m.nodes) {
        byId.set(n.id, { id: n.id, label: n.label, children: [] });
    }
    let root = null;
    for (const n of m.nodes) {
        const tn = byId.get(n.id);
        if (n.parentId === null) {
            root = tn;
        }
        else {
            const parent = byId.get(n.parentId);
            if (parent)
                parent.children.push(tn);
        }
    }
    return root;
}
//# sourceMappingURL=mindmap.js.map